import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkle, FilmStrip, MicrophoneStage, MusicNotes, BookOpen, Smiley } from "@phosphor-icons/react";
import api from "../lib/api";
import Tr from "./Tr";
import MediaThumb from "./MediaThumb";

// Editorial "Handpicked for Tonight" rail. Pulls from /api/handpicked/tonight
// which returns a 6-item mix that rotates by weekday (Sunday → sermons,
// Monday → movies, Tuesday → documentaries, etc.). Designed to give
// returning visitors a "where do I start" answer when the full catalog
// (340 BA films, 290 sermons, 56 cartoons) is too much to scan.
//
// Visual: dark forest banner with corner accent + 6 small tiles. Each
// tile has a kind-tag (Movie / Sermon / Lesson / Music / Cartoon) so the
// user knows what they're tapping into. Tap → navigates to the section
// page with ?play=<id> so the destination auto-opens the player.
const KIND_META = {
  movie:   { label: "Movie",   Icon: FilmStrip,        route: "/movies"        },
  sermon:  { label: "Sermon",  Icon: MicrophoneStage,  route: "/sermons"       },
  lesson:  { label: "Lesson",  Icon: BookOpen,         route: "/sermons"       },
  // music → /gospel-music (the real route, registered in App.js).
  // Previously this said "/gospel" which doesn't exist → React Router
  // 404'd and the user bounced back to home. That was the "tap →
  // glitches → comes right back" issue with Saturday Gospel.
  music:   { label: "Gospel",  Icon: MusicNotes,       route: "/gospel-music"  },
  cartoon: { label: "Cartoon", Icon: Smiley,           route: "/kids"          },
};

export default function HandpickedTonightRail({ filterKind = null, allowedKinds = null, fallbackTitle = null, testid = "handpicked-tonight" }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/handpicked/tonight")
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  }, []);

  if (error) return null;
  if (!data) {
    return (
      <div className="rounded-3xl bg-forest/90 text-cream/70 p-6 text-xs animate-pulse" data-testid={`${testid}-loading`}>
        Loading tonight's picks…
      </div>
    );
  }

  let items = data.items || [];
  // `filterKind` keeps a single kind. `allowedKinds` (array) keeps the
  // intersection. Used by the Sermons page so the handpicked rail
  // never surfaces movie tiles on a sermons surface — user feedback:
  // "you have movies thumbnails and throw back Thursday in sermons".
  if (filterKind) items = items.filter((it) => it.kind === filterKind);
  if (Array.isArray(allowedKinds) && allowedKinds.length)
    items = items.filter((it) => allowedKinds.includes(it.kind));
  if (!items.length) return null;

  // Override the API-provided title when a callsite has stronger
  // semantic context (e.g. /sermons → "Tonight's Sermon Picks" even on
  // a Thursday where the catalog mix was movie-heavy).
  const displayTitle = fallbackTitle && filterKind
    ? fallbackTitle
    : (Array.isArray(allowedKinds) && allowedKinds.length && fallbackTitle)
      ? fallbackTitle
      : data.title;

  const openItem = (it) => {
    const meta = KIND_META[it.kind] || KIND_META.movie;
    // Backend may attach a `collection` hint when an item lives in a
    // sub-catalog (e.g. foundational Black-American films live on
    // /movies/black-american, not the main /movies grid). Route to
    // the right destination so the play deep-link actually opens.
    let route = meta.route;
    if (it.collection === "foundational_black_american") {
      route = "/movies/black-american";
    }
    navigate(`${route}?play=${encodeURIComponent(it.id)}`);
  };

  return (
    <section
      className="rounded-2xl overflow-hidden shadow-lg border border-forest/30"
      data-testid={testid}
    >
      <div
        className="px-4 py-3 md:px-5 md:py-4 text-cream"
        style={{ background: "linear-gradient(135deg,#0d2a1d 0%,#1a3a2a 45%,#c75d3a 130%)" }}
      >
        <div className="flex items-center gap-2 text-[9px] tracking-[0.25em] uppercase opacity-80">
          <Sparkle size={11} weight="fill" /> Handpicked · {data.weekday}
        </div>
        <h2 className="font-heading text-base md:text-lg mt-0.5" data-testid={`${testid}-title`}>
          <Tr>{displayTitle}</Tr>
        </h2>
        <p className="text-cream/80 text-xs mt-0.5"><Tr>{data.subtitle}</Tr></p>
      </div>

      <div className="bg-white p-2 md:p-3">
        {/* Column count clamps to the number of items so a 2-item
            Sermons-only filter doesn't leave 4 empty cells on laptop.
            On small screens we keep 2 columns minimum so tiles stay
            tap-sized. */}
        <div
          className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 md:gap-3"
          style={{
            // Override `lg:grid-cols-6` when there are fewer than 6
            // items so the laptop layout never shows blank slots.
            gridTemplateColumns:
              typeof window !== "undefined" && window.innerWidth >= 1024
                ? `repeat(${Math.min(items.length, 6)}, minmax(0,1fr))`
                : undefined,
          }}
        >
          {items.map((it, idx) => {
            const meta = KIND_META[it.kind] || KIND_META.movie;
            const Icon = meta.Icon;
            // Map the rail's kind label to MediaThumb's `kind` prop so
            // the branded fallback (when both the poster and every
            // YouTube thumbnail variant fail) matches the media type.
            const thumbKind =
              it.kind === "movie" ? "movie" :
              it.kind === "music" ? "music" :
              it.kind === "cartoon" ? "story" :
              "sermon";
            return (
              <button
                key={`${it.kind}-${it.id || idx}`}
                type="button"
                onClick={() => openItem(it)}
                data-testid={`${testid}-tile-${idx}`}
                className="group text-left rounded-2xl overflow-hidden bg-cream-100 hover:bg-cream-200 border border-cream-200 transition active:scale-[0.98]"
              >
                <div className="relative aspect-video bg-forest/15 overflow-hidden">
                  {/* MediaThumb runs a 4-variant YouTube fallback chain
                      (mqdefault → hqdefault → maxresdefault → sddefault)
                      and finally drops to a branded fallback tile when
                      all four miss. Previously this card used a single
                      `<img src=hqdefault>` with no onError, so any
                      transient load failure (data-saver, ad-blocker,
                      momentary network hiccup) left the user staring at
                      Android Chrome's "broken image" icon — the grey
                      square with 3 dots. */}
                  <MediaThumb
                    youtube_id={it.youtube_id}
                    poster_url={it.poster}
                    title={it.title}
                    subtitle={it.speaker || it.artist || it.creator || it.year || ""}
                    kind={thumbKind}
                  />
                  <div className="absolute top-2 left-2 z-10 inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase font-bold text-cream bg-forest/85 px-2 py-1 rounded-full backdrop-blur-sm">
                    <Icon size={10} weight="fill" /> {meta.label}
                  </div>
                </div>
                <div className="p-2 md:p-2.5">
                  <h3 className="text-[11px] md:text-xs font-heading text-forest leading-snug line-clamp-2">
                    {it.title}
                  </h3>
                  {(it.speaker || it.artist || it.creator || it.year) && (
                    <p className="text-[10px] text-muted_ink mt-0.5 truncate">
                      {it.speaker || it.artist || it.creator || it.year}
                    </p>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
